import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Collapse,
  IconButton,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import { formatErrorMessage } from '../utils';

interface MaterialDetail {
  quantity: number;
  type?: string;
  [key: string]: any;
}

interface InventoryItem {
  id: number;
  item: string;
  amount: number;
  state: string;
  created_at: string;
  contract_date: string;
  company_name: string;
  site_name: string;
  purpose: string;
  materials?: Record<string, MaterialDetail>;
}
const formatMaterialDetails = (material: any, type: string) => {
  const parts: string[] = [];

  if (type === 'crown') {
    parts.push(`높이: ${material.height}mm`);
    parts.push(`반지름: ${material.radius}mm`);
  } else if (type === 'left_wing' || type === 'right_wing') {
    parts.push(`너비: ${material.width}mm`);
    parts.push(`반지름: ${material.radius}mm`);
  } else if (type === 'column') {
    parts.push(`높이: ${material.height}mm`);
    parts.push(`두께: ${material.thickness}mm`);
  } else if (type === 'upper') {
    parts.push(`너비: ${material.width}mm`);
    parts.push(`두께: ${material.thickness}mm`);
  } else if (type === 'lower_beam' || type === 'upper_beam') {
    parts.push(`길이: ${material.length}mm`);
    parts.push(`두께: ${material.thickness}mm`);
  } else if (type === 'saddle') {
    parts.push(`상부 R: ${material.upper_radius}mm`);
    parts.push(`하부 R: ${material.lower_radius}mm`);
    parts.push(`너비: ${material.width}mm`);
    parts.push(`높이: ${material.height}mm`);
  }

  if (material.quantity !== undefined) {
    parts.push(`수량: ${material.quantity}개`);
  }

  if (material.type !== undefined) {
    parts.push(`타입: ${material.type === 'new' ? '신규' : '재활용'}`);
  }

  return parts.join(' ');
};

const getMaterialLabel = (type: string) => {
  switch (type) {
    case 'crown': return '크라운';
    case 'left_wing': return '왼쪽 날개';
    case 'right_wing': return '오른쪽 날개';
    case 'column': return '기둥';
    case 'upper': return '상부';
    case 'lower_beam': return '하부 종빔';
    case 'upper_beam': return '상부 종빔';
    case 'saddle': return '새들';
    default: return type;
  }
};

const Row: React.FC<{ item: InventoryItem }> = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{item.company_name}</TableCell>
        <TableCell>{item.site_name}</TableCell>
        <TableCell>{item.item}</TableCell>
        <TableCell>{item.state}</TableCell>
        <TableCell>{item.purpose}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom>자재 상세 정보</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>자재 종류</TableCell>
                    <TableCell>상세 정보</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(item.materials || {}).map(([type, material]) => (
                    <TableRow key={`${item.id}-${type}`}>
                      <TableCell>{getMaterialLabel(type)}</TableCell>
                      <TableCell>{formatMaterialDetails(material,type)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const Inventory: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [receivedInventory, setReceivedInventory] = useState<InventoryItem[]>([]);
  const [billedInventory, setBilledInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [liningDialogOpen, setLiningDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    company: '',
    crownR: '',
    leftWingR: '',
    rightWingR: '',
  });
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchInventory();
  }, []);
  
  const handleFilterChange = (key: 'company' | 'site' | 'item', value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const filterMaterials = (materials: InventoryItem['materials']) => {
    if (!materials) return {};
    return Object.fromEntries(
      Object.entries(materials).filter(([type, mat]) => {
        if (type === 'saddle') return true;
        const qty = typeof mat?.quantity === 'string' ? parseInt(mat.quantity, 10) : mat?.quantity;
        return !isNaN(qty) && qty > 0;
      })
    );
  };
  const handleDownload = async (saleItemId: number) => {
    try {
      const res = await api.get(`/api/sale-items/${saleItemId}/files/design`);
      const { url, filename } = res.data;

      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `도면_${saleItemId}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      enqueueSnackbar('도면 다운로드 실패', { variant: 'error' });
    }
  };


  const fetchInventory = async () => {
    try {
      const [receivedRes, billedRes] = await Promise.all([
        api.get('/api/inventory'),
        api.get('/api/inventory/billed'),
      ]);
      const process = (data: InventoryItem[]) =>
        data.map(item => ({
          ...item,
          materials: filterMaterials(item.materials),
        })).filter(item => Object.keys(item.materials || {}).length > 0);
      setReceivedInventory(process(receivedRes.data));
      setBilledInventory(process(billedRes.data));
    } catch (error) {
      console.error('입고 데이터 조회 중 오류:', error);
      enqueueSnackbar(formatErrorMessage(error), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  const extractRange = (value: string): [number, number] | null => {
    const num = parseInt(value, 10);
    return isNaN(num) ? null : [num, num + 1000];
  };

  const liningItems: InventoryItem[] = [...receivedInventory, ...billedInventory]
    .filter(item => item.purpose === '라이닝폼')
    .filter((item: InventoryItem) => {
      const { company, crownR, leftWingR, rightWingR } = filters;

      const crownRadius = item.materials?.crown?.radius ?? 0;
      const leftRadius = item.materials?.left_wing?.radius ?? 0;
      const rightRadius = item.materials?.right_wing?.radius ?? 0;

      const crownRange = extractRange(crownR);
      const leftRange = extractRange(leftWingR);
      const rightRange = extractRange(rightWingR);

      const matchCompany = item.company_name.includes(company);
      const inCrownRange = !crownRange || (crownRadius >= crownRange[0] && crownRadius < crownRange[1]);
      const inLeftRange = !leftRange || (leftRadius >= leftRange[0] && leftRadius < leftRange[1]);
      const inRightRange = !rightRange || (rightRadius >= rightRange[0] && rightRadius < rightRange[1]);

      return matchCompany && inCrownRange && inLeftRange && inRightRange;
    });


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>재고 현황</Typography>

      <Button
        variant="contained"
        color="primary"
        sx={{ mb: 2 }}
        onClick={() => setLiningDialogOpen(true)}
      >
        라이닝폼 모아보기
      </Button>

      <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)} sx={{ mb: 2 }}>
        <Tab label="공장 재고" />
        <Tab label="현장 재고" />
      </Tabs>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>업체명</TableCell>
              <TableCell>현장</TableCell>
              <TableCell>품목</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>용도</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(tab === 0 ? receivedInventory : billedInventory).length > 0 ? (
              (tab === 0 ? receivedInventory : billedInventory).map(item => <Row key={item.id} item={item} />)
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">항목이 없습니다.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={liningDialogOpen} onClose={() => setLiningDialogOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>라이닝폼 재고 모아보기</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="업체명"
                value={filters.company}
                onChange={(e) => setFilters({ ...filters, company: e.target.value })}
                size="small"
              />
              <TextField
                label="크라운 R"
                value={filters.crownR}
                onChange={(e) => setFilters({ ...filters, crownR: e.target.value })}
                size="small"
                type="number"
              />
              <TextField
                label="좌측 날개 R"
                value={filters.leftWingR}
                onChange={(e) => setFilters({ ...filters, leftWingR: e.target.value })}
                size="small"
                type="number"
              />
              <TextField
                label="우측 날개 R"
                value={filters.rightWingR}
                onChange={(e) => setFilters({ ...filters, rightWingR: e.target.value })}
                size="small"
                type="number"
              />
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2}>업체명</TableCell>
                  <TableCell rowSpan={2}>현장명</TableCell>
                  <TableCell rowSpan={2}>호기</TableCell>
                  <TableCell colSpan={2} align="center">크라운</TableCell>
                  <TableCell colSpan={4} align="center">날개</TableCell>
                  <TableCell colSpan={2} align="center">대차기둥</TableCell>
                  <TableCell colSpan={2} align="center">대차상부</TableCell>
                  <TableCell rowSpan={2}>도면</TableCell>
                </TableRow>
              
                <TableRow>
                  <TableCell>R</TableCell>
                  <TableCell>높이</TableCell>
                  <TableCell>좌측 R</TableCell>
                  <TableCell>좌측 폭</TableCell>
                  <TableCell>우측 R</TableCell>
                  <TableCell>우측 폭</TableCell>
                  <TableCell>규격(두께)</TableCell>
                  <TableCell>높이</TableCell>
                  <TableCell>규격(두께)</TableCell>
                  <TableCell>너비</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {liningItems.map(item => {
                  const crown = item.materials?.crown as MaterialDetail || {};
                  const leftWing = item.materials?.left_wing as MaterialDetail || {};
                  const rightWing = item.materials?.right_wing as MaterialDetail || {};
                  const column = item.materials?.column as MaterialDetail || {};
                  const upper = item.materials?.upper as MaterialDetail || {};

                  return (
                    <>
                    <TableRow key={item.id}>
                      <TableCell>{item.company_name}</TableCell>
                      <TableCell>{item.site_name}</TableCell>
                      <TableCell>{item.item}</TableCell>
                      <TableCell>{crown.radius || '-'}</TableCell>  
                        <TableCell sx={{ borderRight: '2px solid #ccc' }}>{crown.height || '-'}</TableCell>                   
                      <TableCell>{leftWing.radius || '-'}</TableCell>
                      <TableCell>{leftWing.width || '-'}</TableCell>
                      <TableCell>{rightWing.radius || '-'}</TableCell>
                        <TableCell sx={{ borderRight: '2px solid #ccc' }}>{rightWing.width || '-'}</TableCell>
                      <TableCell>{column.thickness || '-'}</TableCell>
                        <TableCell sx={{ borderRight: '2px solid #ccc' }}>{column.height || '-'}</TableCell>
                      <TableCell>{upper.thickness || '-'}</TableCell>
                        <TableCell>{upper.width || '-'}</TableCell>
                        <TableCell sx={{ borderLeft: '2px solid #ccc' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleDownload(item.id)}
                          >
                            다운로드
                          </Button>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLiningDialogOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory;